import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const Onboarding = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [formData, setFormData] = useState({
 owns_dryer: '',
 brand: '',
 chamber_size: '',
 country: '',
 state: '',
 city: '',
 primary_use: '',
 product_focus: '',
 business_name: '',
 website: '',
 social_links: ''
 });
 const [loading, setLoading] = useState(false);

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);

 try {
 await axios.post(`${API}/onboarding`, formData);
 toast.success('Welcome! Your profile is complete.');
 navigate('/dashboard');
 } catch (error) {
 toast.error('Failed to save profile');
 } finally {
 setLoading(false);
 }
 };

 const handleSkip = () => {
 navigate('/dashboard');
 };

 return (
 <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100 py-8 px-4">
 <div className="max-w-2xl mx-auto">
 <Card className="shadow-2xl" data-testid="onboarding-card">
 <CardHeader>
 <CardTitle className="text-3xl">Welcome to Freeze Dry Academy!</CardTitle>
 <CardDescription className="text-lg">
 Help us personalize your experience by sharing a bit about your freeze-drying journey.
 </CardDescription>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="space-y-2">
 <Label>Do you own a freeze dryer?</Label>
 <Select value={formData.owns_dryer} onValueChange={(v) => setFormData({...formData, owns_dryer: v})}>
 <SelectTrigger data-testid="owns-dryer-select">
 <SelectValue placeholder="Select..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="yes">Yes</SelectItem>
 <SelectItem value="no">No</SelectItem>
 <SelectItem value="considering">Considering</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label>Freeze dryer brand (if applicable)</Label>
 <Select value={formData.brand} onValueChange={(v) => setFormData({...formData, brand: v})}>
 <SelectTrigger data-testid="brand-select">
 <SelectValue placeholder="Select brand..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
 <SelectItem value="Other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label>Chamber size/capacity</Label>
 <Select value={formData.chamber_size} onValueChange={(v) => setFormData({...formData, chamber_size: v})}>
 <SelectTrigger data-testid="chamber-size-select">
 <SelectValue placeholder="Select size..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="small">Small</SelectItem>
 <SelectItem value="medium">Medium</SelectItem>
 <SelectItem value="large">Large</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="grid md:grid-cols-3 gap-4">
 <div className="space-y-2">
 <Label>Country</Label>
 <Input
 placeholder="USA"
 value={formData.country}
 onChange={(e) => setFormData({...formData, country: e.target.value})}
 data-testid="country-input"
 />
 </div>
 <div className="space-y-2">
 <Label>State/Province</Label>
 <Input
 placeholder="California"
 value={formData.state}
 onChange={(e) => setFormData({...formData, state: e.target.value})}
 data-testid="state-input"
 />
 </div>
 <div className="space-y-2">
 <Label>City</Label>
 <Input
 placeholder="Los Angeles"
 value={formData.city}
 onChange={(e) => setFormData({...formData, city: e.target.value})}
 data-testid="city-input"
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label>Primary use</Label>
 <Select value={formData.primary_use} onValueChange={(v) => setFormData({...formData, primary_use: v})}>
 <SelectTrigger data-testid="primary-use-select">
 <SelectValue placeholder="Select..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="home">Home</SelectItem>
 <SelectItem value="side_hustle">Side Hustle</SelectItem>
 <SelectItem value="full_time">Full-time Business</SelectItem>
 <SelectItem value="rd_education">R&D/Education</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label>Product focus</Label>
 <Select value={formData.product_focus} onValueChange={(v) => setFormData({...formData, product_focus: v})}>
 <SelectTrigger data-testid="product-focus-select">
 <SelectValue placeholder="Select..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="candy">Candy</SelectItem>
 <SelectItem value="pet_treats">Pet Treats</SelectItem>
 <SelectItem value="fruits_veg">Fruits/Vegetables</SelectItem>
 <SelectItem value="full_meals">Full Meals</SelectItem>
 <SelectItem value="supplements">Supplements/Powders</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label>Business name (optional)</Label>
 <Input
 placeholder="Your business name"
 value={formData.business_name}
 onChange={(e) => setFormData({...formData, business_name: e.target.value})}
 data-testid="business-name-input"
 />
 </div>

 <div className="space-y-2">
 <Label>Website (optional)</Label>
 <Input
 placeholder="https://yourbusiness.com"
 value={formData.website}
 onChange={(e) => setFormData({...formData, website: e.target.value})}
 data-testid="website-input"
 />
 </div>

 <div className="space-y-2">
 <Label>Social links (optional)</Label>
 <Input
 placeholder="Instagram, Facebook, etc."
 value={formData.social_links}
 onChange={(e) => setFormData({...formData, social_links: e.target.value})}
 data-testid="social-links-input"
 />
 </div>

 <div className="flex gap-4">
 <Button type="submit" className="flex-1" disabled={loading} data-testid="submit-onboarding-btn">
 {loading ? 'Saving...' : 'Complete Setup'}
 </Button>
 <Button type="button" variant="outline" onClick={handleSkip} data-testid="skip-onboarding-btn">
 Skip for Now
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
};

export default Onboarding;
